// Centralized environment config. The React app talks to the FastAPI BFF
// at /api/v1/web/* and the public auth router at /api/v1/auth/*.
//
// In development we proxy /api/* via vite (see vite.config.ts), so leaving
// VITE_API_BASE_URL blank works locally. In prod, set both URLs explicitly.

function bool(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined || value === "") return fallback;
  return value === "true" || value === "1";
}

function num(value: string | undefined, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function trimTrailingSlash(s: string): string {
  return s.endsWith("/") ? s.slice(0, -1) : s;
}

const rawApi = (import.meta.env.VITE_API_BASE_URL ?? "").trim();
const rawAuth = (import.meta.env.VITE_AUTH_BASE_URL ?? "").trim();

// Defaults: relative URLs the vite dev proxy (or a same-origin reverse proxy
// in prod) will route to the backend. Mounted prefixes match the BFF router
// declaration in modules/musilizer/musilizer/admin/routes/web.py.
const DEFAULT_API = "/api/v1/web";
const DEFAULT_AUTH = "/api/v1/auth";

export const env = {
  /** BFF base URL (e.g. https://api.vocaler.ai/api/v1/web). */
  apiBaseUrl: trimTrailingSlash(rawApi || DEFAULT_API),
  /** Public-auth base URL (e.g. https://api.vocaler.ai/api/v1/auth). */
  authBaseUrl: trimTrailingSlash(rawAuth || DEFAULT_AUTH),
  /** Set true to bypass the network and use src/mocks. */
  useMocks: bool(import.meta.env.VITE_USE_MOCKS, false),
  mockLatencyMs: num(import.meta.env.VITE_MOCK_LATENCY_MS, 600),
  mockErrorRate: num(import.meta.env.VITE_MOCK_ERROR_RATE, 0),
  defaultLocale: import.meta.env.VITE_DEFAULT_LOCALE ?? "en",
  /**
   * NATIVE visualizer flag — Vocaler's own in-browser (MediaRecorder +
   * canvas) engine, mounted by <VisualizerLane>. Gates the Generate-flow
   * video-stage Visualizer tab AND the Hybrid card in the "Create music
   * video" picker, but NOT the Visualizer card there — that one also
   * lights up on enableVisualizerEmbed below and prefers it when both are
   * on (MV embed submissions use the same render-contract the GPU farm
   * expects; native ones don't — see next comment).
   *
   * Default-OFF since the GPU-farm cutover (#340): the GPU render daemon
   * only speaks the MV state contract, so Vocaler visualizer submissions
   * complete "successfully" with a blank welcome-screen video (verified by
   * smoke test 2026-07-07). Re-enable via VITE_ENABLE_VISUALIZER=true once
   * the BFF submits render-contract payloads (the MV-engine adoption). The
   * BFF has a matching server-side guard (VOCALER_VISUALIZER_SUBMIT_ENABLED)
   * — flip both together.
   */
  enableVisualizer: bool(import.meta.env.VITE_ENABLE_VISUALIZER, false),
  /**
   * MV embed visualizer — the "Visualizer" music-video option (both the
   * Generate-flow video-stage tab and the library's "Create music video"
   * picker) opens MV's embedded editor (iframe + postMessage) and submits
   * the returned design to the GPU farm as mvState. Replaces the retired
   * composer path. This is the flag actually ON in prod today — the GPU
   * farm only speaks MV's contract, so this is the only visualizer engine
   * that renders a real video right now. Requires the backend side to be
   * configured too (MV_EMBED_PARTNER_SECRET + VOCALER_VISUALIZER_SUBMIT_ENABLED)
   * — flip them together.
   */
  enableVisualizerEmbed: bool(import.meta.env.VITE_ENABLE_VISUALIZER_EMBED, false),
} as const;
